import React, { useContext, useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import FinanceV2Layout from "./FinanceV2Layout";
import FinanceV2InfoTip from "./FinanceV2InfoTip";
import { NoteState } from "../../context/notes/NoteState";
import FinanceV2Services from "../../UserServices/FinanceV2Services";
import { METRIC_HELP, CHART_HELP } from "./financeV2HelpTexts";
import { QUICK_ACTIONS } from "./financeV2FeatureGuide";
import DashboardBranchFilter, {
  useDashboardBranch,
} from "../Includes/DashboardBranchFilter";
import "./FinanceV2.css";

const fmt = (n) =>
  Number(n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const SOURCE_LABELS = {
  sales_order: "Sales / checkout",
  expense: "New expenses",
  expense_legacy: "Imported expenses",
  payroll: "Payroll / HR",
  charity: "Charity",
  opening_balance: "Opening balances",
  manual: "Manual journals",
  transfer: "Transfers",
  payment: "Payments",
};

const PIE_COLORS = ["#1a5f4a", "#2d8a6a", "#5cb88a", "#f4a261", "#e76f51", "#264653", "#8ab17d"];

function KpiCard({ label, value, helpKey, children }) {
  const help = METRIC_HELP[helpKey];
  return (
    <div className="fv2-kpi-card">
      <div className="fv2-kpi-label">
        {label}
        {help && <FinanceV2InfoTip title={help.title} body={help.body} source={help.source} />}
      </div>
      <div className="fv2-kpi-value">{value}</div>
      {children}
    </div>
  );
}

export default function FinanceV2Dashboard() {
  const { state } = useContext(NoteState);
  const { branch_id, role } = state;
  const { branch, setBranch, apiBranch } = useDashboardBranch(role, branch_id);
  const [hub, setHub] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  const summary = hub?.summary || {};
  const company = hub?.company || {};
  const counts = hub?.counts || {};
  const salesBf = hub?.sync?.sales_backfill || {};
  const setupDone = company.setup_completed && !summary.setup_required;
  const monthStart = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    .toISOString()
    .split("T")[0];
  const today = new Date().toISOString().split("T")[0];

  const load = async () => {
    setLoading(true);
    try {
      const [dashRes, pnlRes, healthRes] = await Promise.all([
        FinanceV2Services.getDashboard(apiBranch),
        FinanceV2Services.getProfitAndLoss({
          from: monthStart,
          to: today,
          branch_id: apiBranch,
        }).catch(() => null),
        FinanceV2Services.getHealthCheck(apiBranch).catch(() => null),
      ]);
      setHub(dashRes.data?.data?.hub || dashRes.data?.data);
      setPnl(pnlRes?.data?.data || null);
      setHealth(healthRes?.data?.data || null);
    } catch {
      toast.error("Could not load dashboard");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [apiBranch]);

  const pnlChartData = useMemo(
    () => [
      { name: "Revenue", amount: summary.mtd_income ?? 0, fill: "#1a5f4a" },
      { name: "Expenses", amount: summary.mtd_expense ?? 0, fill: "#e76f51" },
      { name: "Net income", amount: summary.mtd_net_income ?? 0, fill: "#2d8a6a" },
    ],
    [summary]
  );

  const sourceChartData = useMemo(() => {
    const raw = hub?.journals_by_source || {};
    return Object.entries(raw)
      .map(([key, count]) => ({
        name: SOURCE_LABELS[key] || key.replace(/_/g, " "),
        value: count,
      }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [hub]);

  const arChartData = useMemo(() => {
    const b = hub?.receivables?.buckets || {};
    return [
      { name: "0–30 days", amount: b.current ?? 0 },
      { name: "31–60", amount: b.days_31_60 ?? b["1_30"] ?? 0 },
      { name: "61–90", amount: b.days_61_90 ?? b["31_60"] ?? 0 },
      { name: "91–120", amount: b.days_91_120 ?? b["61_90"] ?? 0 },
      { name: "120+", amount: b.over_120 ?? b.over_90 ?? 0 },
    ].filter((d) => d.amount > 0);
  }, [hub]);

  const salesSyncData = useMemo(
    () => [
      { name: "Synced", value: salesBf.synced_success ?? 0, fill: "#1a5f4a" },
      { name: "Remaining", value: salesBf.remaining ?? 0, fill: "#f4a261" },
      { name: "Failed", value: salesBf.sync_failed ?? 0, fill: "#e76f51" },
    ],
    [salesBf]
  );

  const topExpenses = (pnl?.expense_lines || []).slice(0, 5);
  const topIncome = (pnl?.income_lines || []).slice(0, 5);

  return (
    <FinanceV2Layout>
      <div className="fv2-dashboard fv2-dashboard-clean">
        <header className="fv2-page-header">
          <div>
            <h1 className="fv2-page-title">Finance overview</h1>
            <p className="fv2-page-sub mb-0">
              {company.name || "Vostro World"}
              {company.currency ? ` · ${company.currency}` : ""} · Month to date
            </p>
          </div>
          <div style={{ minWidth: 180 }}>
            <label className="form-label small mb-1">Branch</label>
            <DashboardBranchFilter
              role={role}
              loginBranchId={branch_id}
              value={branch}
              onChange={setBranch}
            />
          </div>
          {!setupDone && (
            <Link to="/finance-v2/setup" className="btn btn-warning btn-sm">
              Complete setup
            </Link>
          )}
          {health && !health.healthy && (
            <Link to="/finance-v2/import" className="btn btn-outline-danger btn-sm">
              Fix sync issues
            </Link>
          )}
        </header>

        {loading ? (
          <div className="fv2-card text-center py-5">
            <div className="spinner-border text-success" />
          </div>
        ) : (
          <>
            {/* KPI row */}
            <div className="row g-3 mb-4">
              <div className="col-6 col-lg-3">
                <KpiCard
                  label="Net income (MTD)"
                  value={fmt(summary.mtd_net_income)}
                  helpKey="mtd_net_income"
                />
              </div>
              <div className="col-6 col-lg-3">
                <KpiCard label="Revenue (MTD)" value={fmt(summary.mtd_income)} helpKey="mtd_income" />
              </div>
              <div className="col-6 col-lg-3">
                <KpiCard label="Expenses (MTD)" value={fmt(summary.mtd_expense)} helpKey="mtd_expense" />
              </div>
              <div className="col-6 col-lg-3">
                <KpiCard
                  label="Outstanding A/R"
                  value={fmt(hub?.receivables?.total_ar)}
                  helpKey="total_ar"
                >
                  <Link to="/finance-v2/receivables" className="small">
                    View aging →
                  </Link>
                </KpiCard>
              </div>
            </div>

            <div className="row g-3 mb-4">
              <div className="col-6 col-md-3">
                <KpiCard
                  label="Sales synced today"
                  value={summary.sales_synced_today ?? 0}
                  helpKey="sales_synced_today"
                />
              </div>
              <div className="col-6 col-md-3">
                <KpiCard
                  label="Posted journals"
                  value={counts.posted_journals_total ?? 0}
                  helpKey="posted_journals"
                />
              </div>
              <div className="col-6 col-md-3">
                <KpiCard
                  label="Journals this month"
                  value={counts.posted_journals_mtd ?? 0}
                  helpKey="journals_mtd"
                />
              </div>
              <div className="col-6 col-md-3">
                <KpiCard
                  label="Sync issues"
                  value={hub?.sync?.failed_total ?? 0}
                  helpKey="sync_failed"
                >
                  {(hub?.sync?.failed_total ?? 0) > 0 && (
                    <Link to="/finance-v2/import" className="small text-danger">
                      Resolve →
                    </Link>
                  )}
                </KpiCard>
              </div>
            </div>

            {/* Charts */}
            <div className="row g-3 mb-4">
              <div className="col-lg-6">
                <div className="fv2-chart-card">
                  <div className="fv2-chart-title">
                    Income vs expenses (this month)
                    <FinanceV2InfoTip {...CHART_HELP.pnl_overview} />
                  </div>
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={pnlChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => fmt(v)} />
                      <Tooltip formatter={(v) => fmt(v)} />
                      <Bar dataKey="amount" radius={[6, 6, 0, 0]}>
                        {pnlChartData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="fv2-chart-card">
                  <div className="fv2-chart-title">
                    Journal entries by source
                    <FinanceV2InfoTip {...CHART_HELP.journal_sources} />
                  </div>
                  {sourceChartData.length ? (
                    <ResponsiveContainer width="100%" height={260}>
                      <PieChart>
                        <Pie
                          data={sourceChartData}
                          dataKey="value"
                          nameKey="name"
                          cx="50%"
                          cy="50%"
                          outerRadius={90}
                          label={({ name, percent }) =>
                            `${name} ${(percent * 100).toFixed(0)}%`
                          }
                        >
                          {sourceChartData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted small p-4 mb-0">No journals yet — complete setup and import data.</p>
                  )}
                </div>
              </div>
              <div className="col-lg-6">
                <div className="fv2-chart-card">
                  <div className="fv2-chart-title">
                    Receivables aging
                    <FinanceV2InfoTip {...CHART_HELP.ar_aging} />
                  </div>
                  {arChartData.length ? (
                    <ResponsiveContainer width="100%" height={240}>
                      <BarChart data={arChartData} layout="vertical" margin={{ left: 60 }}>
                        <XAxis type="number" tickFormatter={(v) => fmt(v)} />
                        <YAxis type="category" dataKey="name" width={55} tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Bar dataKey="amount" fill="#1a5f4a" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-muted small p-4 mb-0">No outstanding receivables.</p>
                  )}
                  <div className="small text-muted px-3 pb-2">
                    GL A/R balance: {fmt(hub?.receivables?.gl_ar_balance)}
                    <FinanceV2InfoTip {...METRIC_HELP.gl_ar_balance} />
                  </div>
                </div>
              </div>
              <div className="col-lg-6">
                <div className="fv2-chart-card">
                  <div className="fv2-chart-title">
                    Historical sales import
                    <FinanceV2InfoTip {...CHART_HELP.sales_sync} />
                  </div>
                  <ResponsiveContainer width="100%" height={200}>
                    <BarChart data={salesSyncData}>
                      <XAxis dataKey="name" />
                      <YAxis allowDecimals={false} />
                      <Tooltip />
                      <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                        {salesSyncData.map((entry, i) => (
                          <Cell key={i} fill={entry.fill} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                  <div className="text-center pb-2">
                    <Link to="/finance-v2/import" className="btn btn-sm btn-outline-primary">
                      Open import & sync
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Top accounts + quick links */}
            <div className="row g-3">
              <div className="col-lg-5">
                <div className="fv2-card h-100">
                  <h6 className="fw-bold mb-3">
                    Top accounts this month
                    <FinanceV2InfoTip
                      title="Account breakdown"
                      body="Largest income and expense accounts from your posted journals this month."
                      source="Profit & Loss report"
                    />
                  </h6>
                  <p className="small fw-semibold text-success mb-1">Income</p>
                  <ul className="list-unstyled small mb-3">
                    {topIncome.length ? (
                      topIncome.map((r) => (
                        <li key={r.account_id} className="d-flex justify-content-between">
                          <span>
                            {r.code} {r.name}
                          </span>
                          <span>{fmt(r.balance)}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-muted">—</li>
                    )}
                  </ul>
                  <p className="small fw-semibold text-danger mb-1">Expenses</p>
                  <ul className="list-unstyled small mb-0">
                    {topExpenses.length ? (
                      topExpenses.map((r) => (
                        <li key={r.account_id} className="d-flex justify-content-between">
                          <span>
                            {r.code} {r.name}
                          </span>
                          <span>{fmt(r.balance)}</span>
                        </li>
                      ))
                    ) : (
                      <li className="text-muted">—</li>
                    )}
                  </ul>
                </div>
              </div>
              <div className="col-lg-7">
                <div className="fv2-card h-100">
                  <h6 className="fw-bold mb-3">Quick links</h6>
                  <div className="row g-2">
                    {QUICK_ACTIONS.filter((a) =>
                      ["reports", "journals", "coa", "transactions", "bank", "ar", "gl", "setup"].includes(
                        a.id
                      )
                    ).map((a) => (
                      <div className="col-6 col-md-4" key={a.id}>
                        <Link to={a.path} className="fv2-quick-link-sm">
                          <i className={`bi ${a.icon}`} />
                          <span>{a.title}</span>
                        </Link>
                      </div>
                    ))}
                    <div className="col-6 col-md-4">
                      <Link to="/finance-v2/guides" className="fv2-quick-link-sm">
                        <i className="bi bi-book" />
                        <span>Guides & help</span>
                      </Link>
                    </div>
                    <div className="col-6 col-md-4">
                      <Link to="/finance-v2/import" className="fv2-quick-link-sm">
                        <i className="bi bi-cloud-download" />
                        <span>Import & sync</span>
                      </Link>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </FinanceV2Layout>
  );
}
